import { useEffect } from 'react';
import { Controller, type DefaultValues, type SubmitHandler, type UseFormSetError, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { dealerContactSchema, type DealerContactFormInput } from '../schemas/dealerContact.schema';
import { Form, Input, Button, Label } from '@/shared/components';
import { Checkbox } from '@/shared/components/form';

interface DealerContactFormProps {
  onSubmit: SubmitHandler<DealerContactFormInput>;
  onCancel: () => void;
  isLoading?: boolean;
  onSetErrorRef?: (setError: UseFormSetError<DealerContactFormInput>) => void;
}

export function DealerContactForm({ onSubmit, onCancel, isLoading = false, onSetErrorRef }: DealerContactFormProps) {
  const { handleSubmit, control, setError, formState: { isSubmitting } } = useForm<DealerContactFormInput>({
    resolver: zodResolver(dealerContactSchema),
    defaultValues: {
      name: '',
      role: '',
      email: '',
      phone: '',
      notify_agreements: false,
      notify_payments: false,
    } as DefaultValues<DealerContactFormInput>,
  });

  useEffect(() => { onSetErrorRef?.(setError); }, [onSetErrorRef, setError]);

  return (
    <Form className="space-y-4 max-w-lg" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <Label htmlFor="name">Name</Label>
        <Controller
          name="name"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input id="name" placeholder="Full name" value={field.value} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
          )}
        />
      </div>

      <div>
        <Label htmlFor="role">Role <span className="text-mp-muted">(optional)</span></Label>
        <Controller
          name="role"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input id="role" placeholder="e.g. Sales Manager" value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
          )}
        />
      </div>

      <div>
        <Label htmlFor="email">Email <span className="text-mp-muted">(optional)</span></Label>
        <Controller
          name="email"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input id="email" type="email" placeholder="contact@example.com" value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
          )}
        />
      </div>

      <div>
        <Label htmlFor="phone">Phone <span className="text-mp-muted">(optional)</span></Label>
        <Controller
          name="phone"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input id="phone" placeholder="+44 ..." value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
          )}
        />
      </div>

      <div className="space-y-2">
        <Label>Notifications</Label>
        <div className="flex-row items-center gap-2">
          <Controller
            name="notify_agreements"
            control={control}
            render={({ field }) => (
              <Checkbox
                id="notify_agreements"
                checked={field.value ?? false}
                onChange={field.onChange}
                label="Notify on agreements"
                ariaLabel="Notify this contact on agreements"
                disabled={isLoading || isSubmitting}
              />
            )}
          />
        </div>
        <div className="flex-row items-center gap-2">
          <Controller
            name="notify_payments"
            control={control}
            render={({ field }) => (
              <Checkbox
                id="notify_payments"
                checked={field.value ?? false}
                onChange={field.onChange}
                label="Notify on payments"
                ariaLabel="Notify this contact on payments"
                disabled={isLoading || isSubmitting}
              />
            )}
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <Button size="sm" disabled={isLoading || isSubmitting}>
          {isLoading || isSubmitting ? 'Saving...' : 'Add Contact'}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} disabled={isLoading || isSubmitting}>
          Cancel
        </Button>
      </div>
    </Form>
  );
}
